import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import config from 'constants/apiConfig';
import { toast, ToastOptions } from 'react-toastify';
import { noAutoClose } from 'assets/data/config';
import parseErrorMessage from 'utils/parseErrorMessage';

// temporary imports to allow for a smaller refactor size
import { useDispatch } from 'react-redux';
import { getAllIncidentLogs } from 'actions/incidentActions';

// Query keys
export const keys = {
  incidentLogs: 'incidentLogs',
  userWorkLogs: 'userWorkLogs',
};

// Types
interface UpdateWorkLogParams {
  loginDateTime: string;
  logoutDateTime: string;
  workLogId: string;
}

interface UserWorkLog {
  work_log_id: string;
  login_time: string;
  logout_time: string;
  edit_time?: string;
  edited_by_user_guid?: string;
}

// Helper function for error handling
const handleError = (error: any, errorMessage: string): void => {
  const fullErrorMessage = `${errorMessage}\n\n${parseErrorMessage(error)}\n\nIf you continue to experience this error, contact support@disastertech.com`;
  console.error(fullErrorMessage);
  toast.error(fullErrorMessage, noAutoClose as ToastOptions);
};

export const useUpdateIncidentMemberWorkLog = (
  incidentId: string,
  groupGuid: string
) => {
  const queryClient = useQueryClient();

  // temporarily instantite a redux dispatch
  const reduxDispatch = useDispatch();

  return useMutation({
    mutationFn: async ({
      loginDateTime,
      logoutDateTime,
      workLogId,
    }: UpdateWorkLogParams) => {
      const response = await axios(
        config.apiGateway.updateIncidentMemberWorkLog,
        {
          method: 'POST',
          data: {
            work_log_id: workLogId,
            login_time: loginDateTime,
            logout_time: logoutDateTime,
            group_guid: groupGuid,
          },
        }
      );
      return response.data;
    },
    onSuccess: () => {
      // Invalidate both incident logs and user work logs queries
      queryClient.invalidateQueries({
        queryKey: [keys.incidentLogs, incidentId],
      });
      // Invalidate all user work logs queries for this incident
      queryClient.invalidateQueries({
        queryKey: [keys.userWorkLogs, incidentId],
      });

      // temporarily fire the incident logs redux action
      reduxDispatch(getAllIncidentLogs(incidentId));

      toast.success('Work log updated successfully');
    },
    onError: (error: any) => {
      handleError(error, 'ERROR failed to update work log');
    },
  });
};

export const useGetUserWorkLogs = (
  incidentId: string,
  groupGuid: string,
  userId: string,
  incidentMemberId: string
) => {
  return useQuery({
    queryKey: [keys.userWorkLogs, incidentId, userId, incidentMemberId],
    queryFn: async () => {
      const response = await axios(config.apiGateway.incidentMemberWorkLogs, {
        method: 'POST',
        data: {
          incident_id: incidentId,
          user_guid: userId,
          group_guid: groupGuid,
        },
      });
      return response.data as UserWorkLog[];
    },
    enabled: !!incidentId && !!userId && !!incidentMemberId,
  });
};
